/*
  Popover zum Erfassen einer Tagesnotiz im Schichtplan V2.
  Öffnet sich beim Klick auf einen Tages-Header; Löschen entfernt die Notiz des Tages.
*/

import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Popover, Typography } from "@mui/material";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { IconClose, IconDelete } from "../../../../design-system/icons";

const COMMENT_MAX_LENGTH = 1000;

export default function DayCommentPopover({ open, anchorEl, dayLabel, comment = "", onSave, onClose }) {
  const [draftComment, setDraftComment] = useState(comment);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setDraftComment(comment);
    setError("");
  }, [comment, open]);

  const hasExistingComment = Boolean(comment.trim());
  const trimmedDraft = draftComment.trim();
  const isUnchanged = trimmedDraft === comment.trim();

  const submitComment = async (nextComment) => {
    setIsSaving(true);
    setError("");
    try {
      await onSave?.(nextComment);
      onClose?.();
    } catch (saveError) {
      setError("Kommentar konnte nicht gespeichert werden.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      if (!isSaving && !isUnchanged) submitComment(trimmedDraft);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={isSaving ? undefined : onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      PaperProps={{ className: "spv2-day-comment-popover-paper" }}
    >
      <Box className="spv2-day-comment-popover">
        <Box className="spv2-day-comment-head">
          <Box className="spv2-day-comment-head-icon" aria-hidden="true">
            <StickyNote2OutlinedIcon fontSize="small" />
          </Box>
          <Box className="spv2-day-comment-head-copy">
            <Typography className="spv2-day-comment-title">Tagesnotiz</Typography>
            <Typography className="spv2-day-comment-subtitle">{dayLabel}</Typography>
          </Box>
          <IconButton
            className="spv2-day-comment-close-btn"
            size="small"
            aria-label="Tagesnotiz schließen"
            disabled={isSaving}
            onClick={onClose}
          >
            <IconClose fontSize="small" />
          </IconButton>
        </Box>

        <textarea
          className="spv2-day-comment-textarea"
          value={draftComment}
          placeholder="Interne Notiz"
          maxLength={COMMENT_MAX_LENGTH}
          autoFocus
          aria-label={`Tagesnotiz für ${dayLabel}`}
          onChange={(event) => setDraftComment(event.target.value)}
          onKeyDown={handleKeyDown}
        />

        {error ? <Typography className="spv2-day-comment-error">{error}</Typography> : null}

        <Box className="spv2-day-comment-actions">
          {hasExistingComment ? (
            <IconButton
              type="button"
              className="spv2-day-comment-delete-btn"
              size="small"
              aria-label="Tagesnotiz löschen"
              disabled={isSaving}
              onClick={() => submitComment("")}
            >
              <IconDelete fontSize="small" />
            </IconButton>
          ) : (
            <span />
          )}
          <Button
            type="button"
            className="spv2-day-comment-save-btn"
            disabled={isSaving || !trimmedDraft || isUnchanged}
            startIcon={isSaving ? <CircularProgress size={13} color="inherit" /> : null}
            onClick={() => submitComment(trimmedDraft)}
          >
            Speichern
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}
